<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="table-selector-modal-overlay"
      role="presentation"
      @click="closeModal"
    >
      <div
        ref="dialogRef"
        class="table-selector-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="table-modal-title"
        tabindex="-1"
        @click.stop
      >
        <button
          type="button"
          class="table-selector-modal__close"
          aria-label="Cerrar modal"
          @click="closeModal"
        >
          &times;
        </button>

        <h2 id="table-modal-title" class="table-selector-modal__title">
          {{ $t('table.selector.label', 'Seleccionar mesa') }}
        </h2>

        <div class="table-selector-modal__body">
          <TableSelector @table-changed="handleTableChanged" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onUnmounted, ref, watch } from 'vue';
import TableSelector from './TableSelector.vue';
import { focusLandmark, getActiveElement, isAttached } from '@shared/utils/focus';

const props = defineProps<{
  isOpen: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const dialogRef = ref<HTMLElement | null>(null);
let lastFocusedElement: HTMLElement | null = null;
let closeTimer: ReturnType<typeof setTimeout> | null = null;

const clearCloseTimer = () => {
  if (closeTimer !== null) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
};

const closeModal = () => {
  clearCloseTimer();
  emit('close');
};

const handleTableChanged = () => {
  // Give user a brief moment to see selection, then close modal
  clearCloseTimer();
  closeTimer = setTimeout(() => {
    closeTimer = null;
    closeModal();
  }, 300);
};

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// aria-modal="true": keep Tab / Shift+Tab inside the dialog (same pattern as ProfileModal).
const trapTab = (event: KeyboardEvent) => {
  const dialog = dialogRef.value;
  if (!dialog) return;
  const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
  if (focusables.length === 0) {
    event.preventDefault();
    dialog.focus();
    return;
  }
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = getActiveElement();
  if (!dialog.contains(active)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  } else if (event.shiftKey && (active === first || active === dialog)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!props.isOpen) return;
  if (event.key === 'Escape') {
    closeModal();
  } else if (event.key === 'Tab') {
    trapTab(event);
  }
};

// Return focus to the trigger; it may be gone (the "Selecciona una mesa" CTA
// unmounts once a table is chosen), so fall back to the main landmark.
const restoreFocus = () => {
  const target = lastFocusedElement;
  lastFocusedElement = null;
  if (target && isAttached(target) && typeof target.focus === 'function') {
    target.focus();
    return;
  }
  focusLandmark('main');
};

watch(
  () => props.isOpen,
  (open) => {
    if (typeof window === 'undefined') return;
    if (open) {
      lastFocusedElement = getActiveElement();
      window.addEventListener('keydown', handleKeydown);
      void nextTick(() => dialogRef.value?.focus());
    } else {
      clearCloseTimer();
      window.removeEventListener('keydown', handleKeydown);
      if (lastFocusedElement !== null) restoreFocus();
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  clearCloseTimer();
  if (typeof window !== 'undefined') {
    window.removeEventListener('keydown', handleKeydown);
  }
});
</script>

<style scoped>
.table-selector-modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.table-selector-modal {
  position: relative;
  width: 100%;
  max-width: 480px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  padding: 1.5rem;
  animation: modalEnter 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.table-selector-modal__title {
  margin: 0 0 1.25rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--enfa-color-neutral-900, #0f172a);
}

.table-selector-modal__close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  font-size: 1.5rem;
  color: var(--enfa-color-neutral-500, #64748b);
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
}

.table-selector-modal__close:hover {
  background-color: var(--enfa-color-neutral-100, #f1f5f9);
  color: var(--enfa-color-neutral-800, #1e293b);
}

.table-selector-modal__body {
  margin-top: 0.5rem;
}

@keyframes modalEnter {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
</style>
